import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";

const TOKEN_KEY = "km_jwt_token";
const EMAIL_KEY = "km_user_email";

const AuthContext = createContext(null);

// localStorage can throw in private browsing or with site data blocked
const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // the session still works for this page view
  }
};
const removeStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // nothing stored to remove
  }
};

const readSession = () => ({ token: readStorage(TOKEN_KEY) || null, email: readStorage(EMAIL_KEY) });

/**
 * AuthProvider — the one place the signed-in state lives, so the header, cart drawer, footer and
 * checkout all change the moment someone signs in or out. The JWT itself is checked by the
 * middleware on every account request; this only records that the browser holds one.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(readSession);

  // Signing in or out in another tab updates this one too
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === null || event.key === TOKEN_KEY || event.key === EMAIL_KEY) setSession(readSession());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const signIn = useCallback(({ token, email }) => {
    writeStorage(TOKEN_KEY, token);
    writeStorage(EMAIL_KEY, email);
    setSession({ token, email });
  }, []);

  const signOut = useCallback(() => {
    removeStorage(TOKEN_KEY);
    removeStorage(EMAIL_KEY);
    setSession({ token: null, email: "" });
  }, []);

  const value = useMemo(() => {
    const isLoggedIn = Boolean(session.token);
    return {
      token: session.token,
      isLoggedIn,
      user: isLoggedIn ? { email: session.email } : null,
      signIn,
      signOut,
    };
  }, [session, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
